import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { agentPhotoAPI, api } from '@/lib/api';
import ImageCropperSquare from '@/components/common/ImageCropperSquare';

const ACCEPTED_MIMES = 'image/jpeg,image/png,image/webp';
const MAX_SIZE = 25 * 1024 * 1024; // 25 MB

/**
 * Renders the agent's current profile photo (or initials fallback)
 * and offers upload / remove actions. Uploaded pictures are cropped
 * to 512×512 server-side, marked `photo_source='custom'` so Google
 * reconnect will never overwrite them.
 *
 * We fetch the photo bytes via axios (which carries the Bearer token)
 * and expose them as a blob URL — `<img src="">` alone would not
 * authenticate against the streaming endpoint.
 */
export function AgentPhotoUpload({ currentUrl, fallbackInitial = '?', onChanged }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [externalUrl, setExternalUrl] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setBlobUrl(null);
    setExternalUrl(null);
    if (!currentUrl) return;
    // External (Google) URL — use it directly, no auth needed.
    if (/^https?:\/\//i.test(currentUrl) && !currentUrl.includes('/api/agent/')) {
      setExternalUrl(currentUrl);
      return;
    }
    // Internal — fetch with axios to attach the Bearer token, then
    // expose as a blob URL for the <img> tag.
    const path = currentUrl.replace(/^https?:\/\/[^/]+/, '');
    (async () => {
      try {
        const res = await api.get(path.replace(/^\/api/, ''), { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      } catch {
        /* silently fall back to initials */
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [currentUrl]);

  const displaySrc = externalUrl || blobUrl;

  const handlePick = () => inputRef.current?.click();

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED_MIMES.split(',').includes(file.type)) {
      toast.error('Unsupported file type. Please choose a JPG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error('Image is too large. Maximum size is 25 MB.');
      return;
    }
    setPendingFile(file);
  };

  const uploadCropped = async (croppedFile) => {
    if (!croppedFile) return;
    setUploading(true);
    try {
      const res = await agentPhotoAPI.upload(croppedFile);
      const url = res.data?.photo_url;
      toast.success('Profile photo updated');
      setPendingFile(null);
      onChanged?.(url);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove your profile photo? The default initials avatar will be shown instead.')) {
      return;
    }
    setDeleting(true);
    try {
      await agentPhotoAPI.remove();
      toast.success('Profile photo removed');
      onChanged?.(null);
    } catch {
      toast.error('Could not remove photo');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid="agent-photo-upload">
      <div className="relative w-20 h-20 rounded-full bg-slate-900 text-white overflow-hidden flex items-center justify-center text-2xl font-semibold border-2 border-slate-200">
        {displaySrc ? (
          <img
            src={displaySrc}
            alt="Profile"
            className="w-full h-full object-cover"
            data-testid="agent-photo-current"
          />
        ) : (
          <span>{fallbackInitial}</span>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 size={24} className="animate-spin text-white" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_MIMES}
          onChange={handleFile}
          className="hidden"
          data-testid="agent-photo-input"
        />
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePick}
            disabled={uploading || deleting}
            data-testid="agent-photo-upload-btn"
          >
            <Camera size={14} className="mr-1.5" />
            {currentUrl ? 'Change photo' : 'Upload photo'}
          </Button>
          {currentUrl && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={uploading || deleting}
              data-testid="agent-photo-remove-btn"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {deleting ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Trash2 size={14} className="mr-1.5" />
              )}
              Remove
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500">
          JPG, PNG or WebP. Max 25 MB. Crop it to a square before uploading.
        </p>
      </div>
      <ImageCropperSquare
        file={pendingFile}
        onCropped={uploadCropped}
        onCancel={() => setPendingFile(null)}
        title="Adjust profile photo"
      />
    </div>
  );
}
